import { Chess } from "chess.js";
import type { ChessEngine, PositionEval } from "../engine/types";
import { isBookPosition } from "./book";
import { classifyLoss, type MoveClass } from "./classify";
import { cpLossToAccuracy, cpToWinProb, mateToWinProb, winProbToCp } from "./wdl";

const PIECE_VALUE: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 99 };

/** Hamleden sonra taşınan taş, rakip tarafından saldırı altında mı (en prise)? */
function isPieceAttackedAfterMove(fenAfter: string, square: string, byColor: "w" | "b"): boolean {
  try {
    const c = new Chess(fenAfter);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyC = c as any;
    if (typeof anyC.isAttacked === "function") return Boolean(anyC.isAttacked(square, byColor));
    // Yedek (pseudo-legal): saldıranın o kareye hamlesinin olup olmadığına bak
    return (c.moves({ verbose: true }) as { to?: string }[]).some((mv) => mv.to === square);
  } catch {
    return false;
  }
}

export interface AnalyzedMove {
  ply: number; // 0-based yarım hamle indeksi
  moveNumber: number; // 1-based tam hamle numarası
  color: "w" | "b";
  san: string;
  uci: string;
  fenBefore: string;
  fenAfter: string;
  capture: boolean;
  check: boolean;
  onlyLegalMove: boolean;
  bestMoveUci: string;
  bestMoveSan: string;
  bestLineSan: string[]; // motorun önerdiği varyantın ilk hamleleri (SAN)
  evalBeforeCp: number | null; // beyaz POV, 0.01 piyon
  evalAfterCp: number | null; // beyaz POV
  mateBefore: number | null; // beyaz POV işaretli mat uzaklığı
  mateAfter: number | null; // beyaz POV
  winProbBefore: number; // oynayan POV, 0..1
  winProbAfter: number; // oynayan POV, 0..1
  winProbAfterWhite: number; // beyaz POV, 0..1 (eval bar için)
  winProbLoss: number;
  cpLoss: number;
  accuracy: number; // 0..100
  classification: MoveClass;
  gameOver: "checkmate" | "draw" | null;
}

export interface GameAnalysis {
  headers: Record<string, string>;
  initialFen: string;
  result: string;
  totalPlies: number;
  moves: AnalyzedMove[];
  accuracyWhite: number;
  accuracyBlack: number;
  counts: Partial<Record<MoveClass, number>>;
  depth: number;
}

function evalToWinProb(ev: PositionEval | null): number {
  if (!ev) return 0.5;
  if (ev.mateIn != null) return mateToWinProb(ev.mateIn);
  return cpToWinProb(ev.scoreCp ?? 0);
}

function uciToSan(fen: string, uci: string): string {
  if (!uci) return "";
  try {
    const c = new Chess(fen);
    const mv = c.move({
      from: uci.slice(0, 2),
      to: uci.slice(2, 4),
      promotion: uci.length > 4 ? uci[4] : undefined,
    });
    return mv?.san ?? uci;
  } catch {
    return uci;
  }
}

function pvToSan(fen: string, pv: string[], max = 4): string[] {
  const out: string[] = [];
  try {
    const c = new Chess(fen);
    for (const u of pv.slice(0, max)) {
      const mv = c.move({
        from: u.slice(0, 2),
        to: u.slice(2, 4),
        promotion: u.length > 4 ? u[4] : undefined,
      });
      if (!mv) break;
      out.push(mv.san);
    }
  } catch {
    /* eldekinde dur */
  }
  return out;
}

function terminalState(fen: string): "checkmate" | "draw" | null {
  try {
    const c = new Chess(fen);
    if (c.isCheckmate()) return "checkmate";
    if (c.isGameOver()) return "draw";
  } catch {
    /* geçersiz fen -> normal analiz */
  }
  return null;
}

/**
 * PGN'deki her pozisyonu motorla değerlendirir ve hamleleri sınıflandırır.
 * Her FEN tek kez hesaplanır (i. hamlenin after'ı = i+1. hamlenin before'ı).
 */
export async function analyzeGame(
  pgn: string,
  engine: ChessEngine,
  depth = 14,
  onProgress?: (done: number, total: number) => void,
): Promise<GameAnalysis> {
  const game = new Chess();
  try {
    game.loadPgn(pgn.trim());
  } catch (e) {
    throw new Error(`PGN ayrıştırılamadı: ${e instanceof Error ? e.message : String(e)}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headers: Record<string, string> =
    (typeof (game as any).header === "function" ? (game as any).header() : {}) ?? {};
  const history = game.history({ verbose: true });
  if (history.length === 0) throw new Error("PGN'de hamle bulunamadı.");

  const fens: string[] = [history[0].before, ...history.map((m) => m.after)];

  // Her pozisyonu tek kez değerlendir; terminal pozisyonlar motora sorulmaz.
  const evals: (PositionEval | null)[] = [];
  for (let i = 0; i < fens.length; i++) {
    if (terminalState(fens[i])) {
      evals.push(null);
    } else {
      evals.push(await engine.evaluate(fens[i], depth));
    }
    onProgress?.(i + 1, fens.length);
  }

  const moves: AnalyzedMove[] = [];
  const accByColor: { w: number[]; b: number[] } = { w: [], b: [] };
  const counts: Partial<Record<MoveClass, number>> = {};
  let bookPrefix = true; // oyun kitap konumlarından çıkana kadar true

  for (let i = 0; i < history.length; i++) {
    const m = history[i];
    const before = evals[i];
    const after = evals[i + 1];
    const sign = m.color === "w" ? 1 : -1;
    const gameOver = terminalState(m.after);

    const wpBefore = evalToWinProb(before);
    const wpAfterMover =
      gameOver === "checkmate" ? 1 : gameOver === "draw" ? 0.5 : 1 - evalToWinProb(after);

    const winProbLoss = Math.max(0, wpBefore - wpAfterMover);
    const cpLoss = Math.max(0, winProbToCp(wpBefore) - winProbToCp(wpAfterMover));
    const accuracy = cpLossToAccuracy(cpLoss);

    const playedUci = m.from + m.to + (m.promotion ?? "");
    const bestMoveUci = before?.bestMoveUci ?? "";
    const playedMatchesBest = playedUci === bestMoveUci && bestMoveUci !== "";
    let onlyLegalMove = false;
    try {
      onlyLegalMove = new Chess(m.before).moves().length === 1;
    } catch {
      /* false kalır */
    }

    // Açılış kitabı: oyun henüz kitap önekini terk etmediyse ve konum kitapta ise
    if (bookPrefix && (gameOver != null || !isBookPosition(m.before) || !isBookPosition(m.after))) {
      bookPrefix = false;
    }

    // "Brilliant" (parlak hamle) adayı: en iyi hamle + en az at değerinde taş
    // fedası (hamle sonrası en prise) + konum zaten fikir açık kazanç değil.
    const pieceValue = PIECE_VALUE[m.piece] ?? 0;
    const brilliant =
      playedMatchesBest &&
      gameOver == null &&
      pieceValue >= 3 &&
      wpBefore <= 0.85 &&
      isPieceAttackedAfterMove(m.after, m.to, m.color === "w" ? "b" : "w");

    const classification =
      bookPrefix && gameOver == null
        ? "book"
        : classifyLoss(winProbLoss, playedMatchesBest, onlyLegalMove, brilliant);
    counts[classification] = (counts[classification] ?? 0) + 1;
    if (classification !== "book") accByColor[m.color].push(accuracy);

    moves.push({
      ply: i,
      moveNumber: Math.floor(i / 2) + 1,
      color: m.color,
      san: m.san,
      uci: playedUci,
      fenBefore: m.before,
      fenAfter: m.after,
      capture: m.san.includes("x"),
      check: m.san.includes("+") || m.san.includes("#"),
      onlyLegalMove,
      bestMoveUci,
      bestMoveSan: uciToSan(m.before, bestMoveUci),
      bestLineSan: pvToSan(m.before, before?.pvUci ?? []),
      evalBeforeCp: before?.mateIn != null ? null : Math.round((before?.scoreCp ?? 0) * sign),
      evalAfterCp:
        gameOver != null || after == null || after.mateIn != null
          ? null
          : Math.round((after.scoreCp ?? 0) * -sign),
      mateBefore: before?.mateIn != null ? before.mateIn * sign : null,
      mateAfter: gameOver === "checkmate" ? sign : after?.mateIn != null ? after.mateIn * -sign : null,
      winProbBefore: wpBefore,
      winProbAfter: wpAfterMover,
      winProbAfterWhite:
        gameOver === "checkmate" ? (m.color === "w" ? 1 : 0) : gameOver === "draw" ? 0.5 : m.color === "w" ? wpAfterMover : 1 - wpAfterMover,
      winProbLoss,
      cpLoss,
      accuracy,
      classification,
      gameOver,
    });
  }

  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

  return {
    headers,
    initialFen: fens[0],
    result: headers["Result"] ?? "*",
    totalPlies: history.length,
    moves,
    accuracyWhite: Math.round(mean(accByColor.w) * 10) / 10,
    accuracyBlack: Math.round(mean(accByColor.b) * 10) / 10,
    counts,
    depth,
  };
}
